import React, { useState, useEffect, useMemo } from 'react';
import { Search, MapPin, ArrowRight, CheckCircle, XCircle, AlertCircle, Clock, RefreshCw, Users2, ChevronDown, ChevronRight, Calendar } from 'lucide-react';
import axios from 'axios';

interface LocationRequest {
    id: number;
    requester_id: number;
    requester_username: string;
    requester_name?: string;
    requester_avatar?: string;
    target_id: number;
    target_username: string;
    target_name?: string;
    target_avatar?: string;
    family_id: number;
    family_name: string;
    status: string;
    created_at: string;
    expires_at?: string;
    responded_at?: string;
}

interface RequestStats {
    total_requests: number;
    pending: number;
    accepted: number;
    rejected: number;
    expired: number;
    period_requests: number;
    accept_rate: number;
}

const STATUS_META: Record<string, { label: string; color: string; bg: string; border: string; icon: string }> = {
    'pending':  { label: 'Bekliyor',       color: 'text-yellow-700',  bg: 'bg-yellow-50',  border: 'border-yellow-200',  icon: '⏳' },
    'accepted': { label: 'Kabul Edildi',   color: 'text-green-700',   bg: 'bg-green-50',   border: 'border-green-200',   icon: '✅' },
    'rejected': { label: 'Reddedildi',     color: 'text-red-700',     bg: 'bg-red-50',     border: 'border-red-200',     icon: '❌' },
    'expired':  { label: 'Süresi Doldu',   color: 'text-gray-600',    bg: 'bg-gray-50',    border: 'border-gray-200',    icon: '🕐' },
};

const LocationRequestManagement: React.FC = () => {
    const [requests, setRequests] = useState<LocationRequest[]>([]);
    const [stats, setStats] = useState<RequestStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>('');
    const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    useEffect(() => {
        fetchRequests();
        fetchStats();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter]);

    const fetchRequests = async () => {
        setRefreshing(true);
        try {
            const url = statusFilter
                ? `https://api.lifeshare.app/api/v1/admin/location-requests?status_filter=${statusFilter}`
                : 'https://api.lifeshare.app/api/v1/admin/location-requests';
            const response = await axios.get(url);
            setRequests(response.data.requests || []);
        } catch (error) {
            console.error('Location requests fetch error:', error);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const fetchStats = async () => {
        try {
            const response = await axios.get('https://api.lifeshare.app/api/v1/admin/location-requests/stats?days=30');
            setStats(response.data);
        } catch (error) {
            console.error('Location request stats fetch error:', error);
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'pending': return <AlertCircle className="w-3.5 h-3.5" />;
            case 'accepted': return <CheckCircle className="w-3.5 h-3.5" />;
            case 'rejected': return <XCircle className="w-3.5 h-3.5" />;
            case 'expired': return <Clock className="w-3.5 h-3.5" />;
            default: return null;
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'pending': return 'bg-yellow-100 text-yellow-700';
            case 'accepted': return 'bg-green-100 text-green-700';
            case 'rejected': return 'bg-red-100 text-red-700';
            case 'expired': return 'bg-gray-100 text-gray-600';
            default: return 'bg-gray-100 text-gray-600';
        }
    };

    const getStatusText = (status: string) => {
        switch (status) {
            case 'pending': return 'Bekliyor';
            case 'accepted': return 'Kabul';
            case 'rejected': return 'Red';
            case 'expired': return 'Süresi Doldu';
            default: return status;
        }
    };

    const getTimeAgo = (timestamp: string) => {
        const ts = timestamp.endsWith('Z') ? timestamp : timestamp + 'Z';
        const diff = (Date.now() - new Date(ts).getTime()) / 1000;
        if (diff < 0) return 'Az önce';
        if (diff < 60) return 'Az önce';
        if (diff < 3600) return `${Math.floor(diff / 60)} dk`;
        if (diff < 86400) return `${Math.floor(diff / 3600)} sa`;
        return `${Math.floor(diff / 86400)} gün`;
    };

    const filteredRequests = requests.filter(req => {
        const matchesSearch =
            req.requester_username.toLowerCase().includes(searchText.toLowerCase()) ||
            req.target_username.toLowerCase().includes(searchText.toLowerCase()) ||
            req.family_name.toLowerCase().includes(searchText.toLowerCase()) ||
            (req.requester_name || '').toLowerCase().includes(searchText.toLowerCase()) ||
            (req.target_name || '').toLowerCase().includes(searchText.toLowerCase());

        let matchesDate = true;
        if (dateFrom) matchesDate = new Date(req.created_at) >= new Date(dateFrom + 'T00:00:00');
        if (dateTo && matchesDate) matchesDate = new Date(req.created_at) <= new Date(dateTo + 'T23:59:59');

        return matchesSearch && matchesDate;
    });

    const groupedRequests = useMemo(() => {
        const groups: Record<number, LocationRequest[]> = {};
        for (const r of filteredRequests) {
            if (!groups[r.requester_id]) groups[r.requester_id] = [];
            groups[r.requester_id].push(r);
        }
        const sortedKeys = Object.keys(groups).map(Number).sort((a, b) => {
            const aTime = new Date(groups[a][0].created_at).getTime();
            const bTime = new Date(groups[b][0].created_at).getTime();
            return bTime - aTime;
        });
        return { groups, sortedKeys };
    }, [filteredRequests]);

    const toggleGroup = (userId: number) => {
        setExpandedGroups(prev => {
            const next = new Set(prev);
            if (next.has(userId)) next.delete(userId);
            else next.add(userId);
            return next;
        });
    };

    const filters = [
        { key: '', label: 'Tümü' },
        { key: 'pending', label: 'Bekleyen' },
        { key: 'accepted', label: 'Kabul' },
        { key: 'rejected', label: 'Red' },
        { key: 'expired', label: 'Süresi Doldu' },
    ];

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="w-5 h-5 border-2 border-purple-300 border-t-purple-600 rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="max-w-5xl mx-auto space-y-6">

                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-semibold text-gray-900">Konum İstekleri</h1>
                        <p className="text-sm text-gray-500 mt-1">Kullanıcılar arası konum paylaşım istekleri</p>
                    </div>
                    <button
                        onClick={fetchRequests}
                        disabled={refreshing}
                        className="p-2.5 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-colors"
                    >
                        <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
                    </button>
                </div>

                {/* Stats */}
                {stats && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-2xl p-4">
                            <p className="text-2xl font-bold text-purple-700">{stats.total_requests}</p>
                            <p className="text-xs text-purple-600">Toplam İstek</p>
                        </div>
                        <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 border border-yellow-200 rounded-2xl p-4">
                            <p className="text-2xl font-bold text-yellow-700">{stats.pending}</p>
                            <p className="text-xs text-yellow-600">Bekleyen</p>
                        </div>
                        <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-2xl p-4">
                            <p className="text-2xl font-bold text-green-700">{stats.accepted}</p>
                            <p className="text-xs text-green-600">Kabul Edilen</p>
                        </div>
                        <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-2xl p-4">
                            <p className="text-2xl font-bold text-blue-700">{stats.accept_rate}%</p>
                            <p className="text-xs text-blue-600">Kabul Oranı</p>
                        </div>
                    </div>
                )}

                {/* Search & Filters */}
                <div className="bg-white rounded-2xl p-4 space-y-3">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Kullanıcı veya çevre ara..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            className="w-full pl-10 pr-4 py-3 bg-gray-50 rounded-xl text-sm placeholder-gray-400 outline-none focus:ring-2 focus:ring-purple-500"
                        />
                    </div>

                    <div className="flex gap-2 overflow-x-auto pb-1">
                        {filters.map(f => (
                            <button
                                key={f.key}
                                onClick={() => setStatusFilter(f.key)}
                                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${statusFilter === f.key ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                            >
                                {f.label}
                            </button>
                        ))}
                    </div>

                    {/* Date Filter */}
                    <div className="flex flex-col sm:flex-row gap-3 items-end">
                        <div className="flex-1 w-full">
                            <label className="text-xs text-gray-500 mb-1 block">Başlangıç</label>
                            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-full px-3 py-2.5 bg-gray-50 rounded-xl text-sm text-gray-700 outline-none focus:ring-2 focus:ring-purple-500" />
                        </div>
                        <div className="flex-1 w-full">
                            <label className="text-xs text-gray-500 mb-1 block">Bitiş</label>
                            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-full px-3 py-2.5 bg-gray-50 rounded-xl text-sm text-gray-700 outline-none focus:ring-2 focus:ring-purple-500" />
                        </div>
                        <button
                            onClick={() => { setDateFrom(''); setDateTo(''); }}
                            className="px-4 py-2.5 bg-gray-100 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-200 transition-colors whitespace-nowrap"
                        >
                            Temizle
                        </button>
                    </div>
                </div>

                {/* Requests — Grouped by Requester */}
                {filteredRequests.length === 0 ? (
                    <div className="bg-white rounded-2xl p-12 text-center">
                        <MapPin className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500">Konum isteği bulunamadı</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {groupedRequests.sortedKeys.map((userId) => {
                            const userReqs = groupedRequests.groups[userId];
                            const firstReq = userReqs[0];
                            const isExpanded = expandedGroups.has(userId);
                            const pendingCount = userReqs.filter(r => r.status === 'pending').length;
                            const acceptedCount = userReqs.filter(r => r.status === 'accepted').length;
                            const latestTime = userReqs[0].created_at;

                            return (
                                <div key={userId} className="bg-white rounded-2xl border border-purple-100 overflow-hidden">
                                    {/* Group Header */}
                                    <button
                                        onClick={() => toggleGroup(userId)}
                                        className="w-full flex items-center gap-3 p-4 bg-purple-50/50 hover:bg-purple-50 transition-all"
                                    >
                                        {firstReq.requester_avatar ? (
                                            <img src={`https://api.lifeshare.app${firstReq.requester_avatar}`} alt="" className="w-11 h-11 rounded-full object-cover flex-shrink-0" />
                                        ) : (
                                            <div className="w-11 h-11 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 font-semibold flex-shrink-0">
                                                {(firstReq.requester_name || firstReq.requester_username)[0].toUpperCase()}
                                            </div>
                                        )}
                                        <div className="flex-1 text-left">
                                            <div className="flex items-center gap-2">
                                                <span className="font-semibold text-gray-900 text-sm">{firstReq.requester_name || firstReq.requester_username}</span>
                                                <span className="px-2 py-0.5 bg-purple-50 text-purple-700 rounded-full text-xs font-medium border border-purple-200">
                                                    {userReqs.length} istek
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500">
                                                {pendingCount > 0 && (
                                                    <span className="flex items-center gap-1 text-yellow-600">
                                                        <AlertCircle className="w-3 h-3" />
                                                        {pendingCount} bekleyen
                                                    </span>
                                                )}
                                                {acceptedCount > 0 && (
                                                    <span className="flex items-center gap-1 text-green-600">
                                                        <CheckCircle className="w-3 h-3" />
                                                        {acceptedCount} kabul
                                                    </span>
                                                )}
                                                <span className="flex items-center gap-1">
                                                    <Clock className="w-3 h-3" />
                                                    En son: {getTimeAgo(latestTime)}
                                                </span>
                                            </div>
                                        </div>
                                        {isExpanded ? <ChevronDown className="w-5 h-5 text-gray-400" /> : <ChevronRight className="w-5 h-5 text-gray-400" />}
                                    </button>

                                    {/* Group Items */}
                                    {isExpanded && (
                                        <div className="divide-y divide-gray-50">
                                            {userReqs.map((req) => {
                                                const meta = STATUS_META[req.status] || STATUS_META['expired'];
                                                return (
                                                    <div key={req.id} className={`p-4 hover:bg-gray-50/50 transition-colors border-l-4 ${meta.border.replace('border-', 'border-l-')}`}>
                                                        {/* Status Badge */}
                                                        <div className="flex items-center justify-between mb-3">
                                                            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-xl text-xs font-medium ${meta.bg} ${meta.color}`}>
                                                                {meta.icon} {meta.label}
                                                            </span>
                                                            <span className="text-xs text-gray-400">{getTimeAgo(req.created_at)}</span>
                                                        </div>

                                                        {/* Users Row */}
                                                        <div className="flex items-center gap-2">
                                                            {/* Requester */}
                                                            <div className="flex-1 flex items-center gap-2 min-w-0">
                                                                {req.requester_avatar ? (
                                                                    <img src={`https://api.lifeshare.app${req.requester_avatar}`} alt="" className="w-9 h-9 rounded-xl object-cover flex-shrink-0" />
                                                                ) : (
                                                                    <div className="w-9 h-9 rounded-xl bg-purple-100 flex items-center justify-center text-purple-600 font-medium text-sm flex-shrink-0">
                                                                        {(req.requester_name || req.requester_username)[0].toUpperCase()}
                                                                    </div>
                                                                )}
                                                                <div className="min-w-0 flex-1">
                                                                    <p className="font-medium text-gray-900 text-sm truncate">{req.requester_name || req.requester_username}</p>
                                                                    <p className="text-xs text-gray-500 truncate">@{req.requester_username}</p>
                                                                </div>
                                                            </div>

                                                            {/* Arrow */}
                                                            <div className="p-1.5 bg-gray-100 rounded-full flex-shrink-0">
                                                                <ArrowRight className="w-3.5 h-3.5 text-gray-400" />
                                                            </div>

                                                            {/* Target */}
                                                            <div className="flex-1 flex items-center gap-2 min-w-0">
                                                                {req.target_avatar ? (
                                                                    <img src={`https://api.lifeshare.app${req.target_avatar}`} alt="" className="w-9 h-9 rounded-xl object-cover flex-shrink-0" />
                                                                ) : (
                                                                    <div className="w-9 h-9 rounded-xl bg-blue-100 flex items-center justify-center text-blue-600 font-medium text-sm flex-shrink-0">
                                                                        {(req.target_name || req.target_username)[0].toUpperCase()}
                                                                    </div>
                                                                )
                                                                }
                                                                <div className="min-w-0 flex-1">
                                                                    <p className="font-medium text-gray-900 text-sm truncate">{req.target_name || req.target_username}</p>
                                                                    <p className="text-xs text-gray-500 truncate">@{req.target_username}</p>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Footer */}
                                                        <div className="mt-3 pt-3 border-t border-gray-50 flex items-center justify-between">
                                                            <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
                                                                <Users2 className="w-3.5 h-3.5" />
                                                                {req.family_name}
                                                            </span>
                                                            <span className="text-xs text-gray-400">
                                                                {new Date(req.created_at).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' })} • {new Date(req.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                                                            </span>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

            </div>
        </div>
    );
};

export default LocationRequestManagement;
